import React from "react";
import { Box, Typography } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { PeriodMetric } from "../../models/models";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface CompletionTrendChartProps {
  completionTrend: PeriodMetric[];
  createdTrend: PeriodMetric[];
}

const CompletionTrendChart: React.FC<CompletionTrendChartProps> = ({
  completionTrend,
  createdTrend,
}) => {
  const { translate } = useTranslation("operations");

  const labels = completionTrend.map(p => p.label);

  const data = {
    labels,
    datasets: [
      {
        label: translate("analytics.totalCompleted"),
        data: completionTrend.map(p => p.value),
        backgroundColor: "rgba(52, 152, 219, 0.8)",
        borderRadius: 4,
        maxBarThickness: 20,
      },
      {
        label: translate("analytics.totalCreated"),
        data: createdTrend.map(p => p.value),
        backgroundColor: "rgba(156, 163, 175, 0.5)",
        borderRadius: 4,
        maxBarThickness: 20,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          font: { family: "var(--font-sans)", size: 12 },
          color: dt.textSecondary,
          usePointStyle: true,
          pointStyle: "rectRounded" as const,
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: "#1a1a2e",
        titleFont: { family: "var(--font-sans)", size: 13 },
        bodyFont: { family: "var(--font-mono)", size: 12 },
        cornerRadius: 8,
        padding: 10,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { family: "var(--font-sans)", size: 11 },
          color: dt.textMuted,
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: {
          font: { family: "var(--font-mono)", size: 11 },
          color: dt.textMuted,
          stepSize: 1,
        },
      },
    },
  };

  return (
    <Box
      sx={{
        background: dt.cardBg,
        borderRadius: dt.radius,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        p: 2.5,
      }}
    >
      <Typography
        sx={{
          fontFamily: dt.font,
          fontSize: "15px",
          fontWeight: 600,
          color: dt.textPrimary,
          mb: 2,
        }}
      >
        {translate("analytics.completionTrend")}
      </Typography>
      <Box sx={{ height: 280 }}>
        <Bar data={data} options={options} />
      </Box>
    </Box>
  );
};

export default CompletionTrendChart;
